import { isValidElement, type ReactNode } from 'react';

/**
 * Tabular export, in the browser.
 *
 * Every list screen exports the rows it is currently showing, in the order and with the columns
 * the reader can see - what you export is what is on the screen, filters and sort included. That
 * is the property people actually want from an export button, and it is why this works from the
 * rendered column definitions rather than from a second server-side projection that would drift
 * out of step with them.
 *
 * The ceiling is the page in hand: the register itself is server-paged and is never all in the
 * browser.
 * ponytail: browser-side generation, move to a streaming server endpoint if anyone needs to export
 * the whole register at six figures of rows.
 */

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportColumn<T> {
  id: string;
  header: string;
  /** The plain value for this cell. Falls back to the rendered cell's text. */
  value: (row: T) => string;
}

export interface ExportRequest<T> {
  /** File name without an extension. Sanitised before use. */
  filename: string;
  /** Shown as the document title in the PDF and as the worksheet name. */
  title?: string;
  columns: ExportColumn<T>[];
  rows: T[];
}

/**
 * The text inside a rendered cell.
 *
 * Columns render React, not strings, so an export has to recover the words. Walking the element
 * tree keeps every existing table exportable without each one declaring a parallel set of plain
 * accessors - a column only needs `exportValue` when the visible text is not the value (an icon
 * standing in for a status, a relative date where the export wants the absolute one).
 */
export function nodeToText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).filter(Boolean).join(' ');
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode } | null;
    return nodeToText(props?.children);
  }
  return '';
}

/** Trims a title down to something a filesystem will accept on every platform. */
function safeFilename(name: string): string {
  const cleaned = name
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase();
  return cleaned || 'export';
}

/** `2026-09-12`, so exported files sort chronologically in a folder. */
function stamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function toMatrix<T>(request: ExportRequest<T>): string[][] {
  return [
    request.columns.map((column) => column.header),
    ...request.rows.map((row) => request.columns.map((column) => column.value(row))),
  ];
}

/**
 * CSV, quoted per RFC 4180.
 *
 * The leading BOM is what makes Excel on Windows read the file as UTF-8 rather than as the local
 * code page, which is the difference between a name with an accent in it surviving the round trip
 * and arriving as mojibake.
 */
export function toCsvText<T>(request: ExportRequest<T>): string {
  const escape = (cell: string) =>
    /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

  return toMatrix(request)
    .map((row) => row.map(escape).join(','))
    .join('\r\n');
}

function exportCsv<T>(request: ExportRequest<T>): void {
  const csv = toCsvText(request);

  download(
    new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }),
    `${safeFilename(request.filename)}-${stamp()}.csv`,
  );
}

/** A real workbook, so Excel gets a frozen header and sized columns rather than a parsed text file. */
async function exportXlsx<T>(request: ExportRequest<T>): Promise<void> {
  const XLSX = await import('xlsx');
  const matrix = toMatrix(request);
  const sheet = XLSX.utils.aoa_to_sheet(matrix);

  // Column widths from the longest cell, capped: an unbounded width lets one long description
  // push every other column off the screen.
  sheet['!cols'] = request.columns.map((column, index) => ({
    wch: Math.min(
      48,
      Math.max(
        column.header.length + 2,
        ...matrix.slice(1).map((row) => (row[index] ?? '').length + 2),
      ),
    ),
  }));
  sheet['!freeze'] = { xSplit: '0', ySplit: '1' };

  const book = XLSX.utils.book_new();
  // Excel refuses a sheet name over 31 characters or containing []:*?/\.
  XLSX.utils.book_append_sheet(
    book,
    sheet,
    (request.title ?? 'Export').replace(/[[\]:*?/\\]/g, '').slice(0, 31) || 'Export',
  );
  XLSX.writeFile(book, `${safeFilename(request.filename)}-${stamp()}.xlsx`);
}

/**
 * PDF, landscape.
 *
 * Landscape because these are wide operational tables; portrait squeezes eight columns into
 * something nobody can read, which defeats the point of a PDF someone means to circulate.
 */
async function exportPdf<T>(request: ExportRequest<T>): Promise<void> {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const autoTable = autoTableModule.default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const title = request.title ?? request.filename;

  doc.setFontSize(14);
  doc.text(title, 40, 40);
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    `${request.rows.length.toLocaleString()} rows · exported ${new Date().toLocaleString()}`,
    40,
    56,
  );

  autoTable(doc, {
    startY: 70,
    head: [request.columns.map((column) => column.header)],
    body: request.rows.map((row) => request.columns.map((column) => column.value(row))),
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [47, 95, 224], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 251] },
    margin: { left: 40, right: 40 },
  });

  doc.save(`${safeFilename(request.filename)}-${stamp()}.pdf`);
}

export async function exportTable<T>(
  format: ExportFormat,
  request: ExportRequest<T>,
): Promise<void> {
  if (request.rows.length === 0) throw new Error('There is nothing to export.');
  if (format === 'csv') return exportCsv(request);
  if (format === 'xlsx') return exportXlsx(request);
  return exportPdf(request);
}

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  xlsx: 'Excel (.xlsx)',
  csv: 'CSV (.csv)',
  pdf: 'PDF (.pdf)',
};
